import type { FurnitureSpriteKey } from '@/sprites/furniture';

/**
 * Placed furniture, per area.
 *
 * Placement is direct — face a floor tile, press Use with furniture in hand —
 * rather than a separate ghost-and-confirm mode. A mode would need its own
 * controls, its own cancel, and its own way of being escaped from on a phone;
 * one tile at a time reads the same and costs none of that.
 */

export type FurnitureId = 'table' | 'chair' | 'lamp' | 'plantPot' | 'bookshelf';

export interface FurnitureDefinition {
  readonly id: FurnitureId;
  readonly name: string;
  readonly sprite: FurnitureSpriteKey;
  /** Whether it blocks movement. Rugs and lamps do not; tables do. */
  readonly solid: boolean;
}

export const FURNITURE: Readonly<Record<FurnitureId, FurnitureDefinition>> = {
  table: { id: 'table', name: 'Table', sprite: 'table', solid: true },
  chair: { id: 'chair', name: 'Chair', sprite: 'chair', solid: false },
  lamp: { id: 'lamp', name: 'Lamp', sprite: 'lamp', solid: false },
  plantPot: { id: 'plantPot', name: 'Potted Plant', sprite: 'plantPot', solid: true },
  bookshelf: { id: 'bookshelf', name: 'Bookshelf', sprite: 'bookshelf', solid: true },
};

export function isFurnitureId(value: unknown): value is FurnitureId {
  return typeof value === 'string' && value in FURNITURE;
}

export interface PlacedFurniture {
  readonly id: FurnitureId;
  readonly x: number;
  readonly y: number;
}

export class FurnitureLayer {
  private readonly placed = new Map<string, FurnitureId>();

  get size(): number {
    return this.placed.size;
  }

  at(x: number, y: number): FurnitureId | undefined {
    return this.placed.get(key(x, y));
  }

  /** True if something solid stands here. */
  isSolidAt(x: number, y: number): boolean {
    const id = this.at(x, y);
    return id !== undefined && FURNITURE[id].solid;
  }

  place(x: number, y: number, id: FurnitureId): boolean {
    if (this.placed.has(key(x, y))) return false;
    this.placed.set(key(x, y), id);
    return true;
  }

  /** Pick a piece back up, returning what it was. */
  remove(x: number, y: number): FurnitureId | null {
    const id = this.at(x, y);
    if (id === undefined) return null;
    this.placed.delete(key(x, y));
    return id;
  }

  entries(): PlacedFurniture[] {
    return Array.from(this.placed, ([coordinate, id]) => {
      const [x, y] = coordinate.split(',').map(Number) as [number, number];
      return { id, x, y };
    });
  }

  toSave(): PlacedFurniture[] {
    return this.entries();
  }

  static fromSave(entries: unknown): FurnitureLayer {
    const layer = new FurnitureLayer();
    if (!Array.isArray(entries)) return layer;

    for (const entry of entries as PlacedFurniture[]) {
      if (!entry || !isFurnitureId(entry.id)) continue;
      if (!Number.isInteger(entry.x) || !Number.isInteger(entry.y)) continue;
      layer.placed.set(key(entry.x, entry.y), entry.id);
    }
    return layer;
  }
}

function key(x: number, y: number): string {
  return `${x},${y}`;
}
